import { kortKlant } from './klantAfkortingen';
import { AFGERONDE_STATUSSEN } from './taakStatus';
// Gedeelde capaciteitsberekening — gebruikt door Planner To-Do én Capaciteit-overzicht,
// zodat beide pagina's IDENTIEKE percentages tonen.
//
// De regels hieronder zijn de INGEBOUWDE standaard. De planner kan ze aanpassen
// (Capaciteit → "Zo rekenen we dit uit", of Admin → Planning-regels); die waarden
// komen via GET /Api/planning-rules binnen en gaan als `rules` mee in de berekening.
// Deze module is daarmee de enige plek waar de standaardwaarden staan — de engine
// bewaart alleen wat er ván de standaard afwijkt.

export const DEFAULT_CAPACITY_RULES = {
  /** Een volle werkdag = 100% bezetting. */
  hoursPerDay: 8,
  /** Inschatting per taaktype als UrenWerk leeg is. */
  hoursByType: { 'Draaidag': 8, 'Vrij/intern': 8, 'Vrije dag': 8, 'Vanallesuurtje': 1 },
  /** Inschatting voor taaktypes die hierboven niet staan. */
  fallbackHours: 4,
  /** Taken met deze status/prio tellen niet mee. */
  excludedStatuses: [...AFGERONDE_STATUSSEN],
  /** Een 'Vanallesuurtje' telt niet mee op een dag waarop iemand een draaidag heeft. */
  skipVanallesuurtjeOnDraaidag: true,
  /**
   * Hoe een meerdaagse taak zijn uren over zijn dagen verdeelt.
   *
   * 'gelijk'   — uren / aantal dagen, ongeacht wat er al staat.
   * 'opvullen' — naar rato van de rúimte die elke dag nog heeft.
   *
   * Waarom 'opvullen' de standaard is: staat er op donderdag al 6u vast en plan je er
   * een montage van 10u over donderdag+vrijdag overheen, dan is dat in de praktijk 2u
   * op donderdag en 8u op vrijdag — twee volle dagen. 'gelijk' maakte er 11u (138%) en
   * 5u (63%) van: een donderdag die roder oogt dan hij is en een vrijdag die nog ruimte
   * lijkt te hebben die er niet is.
   */
  verdeling: 'opvullen',
};

/** De twee verdeelmodellen, met de tekst die de instellingen-UI toont. */
export const VERDELINGEN = {
  opvullen: 'Naar rato van de ruimte die elke dag nog heeft',
  gelijk: 'Gelijk over alle dagen, ongeacht wat er al staat',
};

/**
 * Taaktypes waarvoor de UI een eigen inschatting laat instellen.
 * 'Draaidag' en 'Vrije dag' staan hier bewust NIET meer bij: die zijn geen inschatting
 * maar een dagstáát (vol resp. niet beschikbaar, zie `computeDayStates`). Een knop die
 * doet alsof je een vrije dag op 4 uur kunt zetten is een knop die niets doet.
 */
export const CONFIGURABLE_TYPES = ['Vrij/intern', 'Vanallesuurtje'];
/** Statussen die je kunt uitsluiten (uit de Airtable-statuslijst, zie CLAUDE.md). */
export const EXCLUDABLE_STATUSES = [...AFGERONDE_STATUSSEN, 'Nog niet van toepassing'];

// Back-compat: elders in de code nog gebruikt als "de standaard".
export const HOURS_PER_DAY = DEFAULT_CAPACITY_RULES.hoursPerDay;
export const DEFAULT_HOURS_BY_TYPE = DEFAULT_CAPACITY_RULES.hoursByType;

const positief = (v, standaard) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : standaard;
};

/**
 * Vul een (deels ingevulde, mogelijk onbetrouwbare) regelset aan tot een complete set.
 * Alles wat ontbreekt of onzin is, valt terug op de standaard — de berekening mag
 * nooit stukgaan op een halve instelling.
 */
export function normalizeCapacityRules(raw) {
  const r = raw && typeof raw === 'object' ? raw : {};
  const hoursByType = { ...DEFAULT_CAPACITY_RULES.hoursByType };
  if (r.hoursByType && typeof r.hoursByType === 'object') {
    Object.entries(r.hoursByType).forEach(([type, uren]) => {
      const n = Number(uren);
      if (type && Number.isFinite(n) && n >= 0) hoursByType[type] = n;
    });
  }
  return {
    hoursPerDay: positief(r.hoursPerDay, DEFAULT_CAPACITY_RULES.hoursPerDay),
    hoursByType,
    fallbackHours: positief(r.fallbackHours, DEFAULT_CAPACITY_RULES.fallbackHours),
    excludedStatuses: Array.isArray(r.excludedStatuses)
      ? r.excludedStatuses.map(String).filter(Boolean)
      : [...DEFAULT_CAPACITY_RULES.excludedStatuses],
    skipVanallesuurtjeOnDraaidag: r.skipVanallesuurtjeOnDraaidag !== false,
    verdeling: VERDELINGEN[r.verdeling] ? r.verdeling : DEFAULT_CAPACITY_RULES.verdeling,
  };
}

/** Alleen de afwijkingen t.o.v. de standaard — dat bewaren we in de instellingen. */
export function diffFromDefaults(rules) {
  const r = normalizeCapacityRules(rules);
  const d = DEFAULT_CAPACITY_RULES;
  const uit = {};
  if (r.hoursPerDay !== d.hoursPerDay) uit.hoursPerDay = r.hoursPerDay;
  if (r.fallbackHours !== d.fallbackHours) uit.fallbackHours = r.fallbackHours;
  if (r.skipVanallesuurtjeOnDraaidag !== d.skipVanallesuurtjeOnDraaidag) {
    uit.skipVanallesuurtjeOnDraaidag = r.skipVanallesuurtjeOnDraaidag;
  }
  if (r.verdeling !== d.verdeling) uit.verdeling = r.verdeling;
  const types = {};
  Object.entries(r.hoursByType).forEach(([t, u]) => { if (d.hoursByType[t] !== u) types[t] = u; });
  if (Object.keys(types).length) uit.hoursByType = types;
  const zelfdeStatussen =
    r.excludedStatuses.length === d.excludedStatuses.length &&
    r.excludedStatuses.every((s) => d.excludedStatuses.includes(s));
  if (!zelfdeStatussen) uit.excludedStatuses = r.excludedStatuses;
  return uit;
}

/** Leesbare uitleg van de regels — met de wérkelijk ingestelde waarden erin. */
export function describeCapacityRules(rules) {
  const r = normalizeCapacityRules(rules);
  const typeUitleg = CONFIGURABLE_TYPES
    .filter((t) => r.hoursByType[t] !== undefined)
    .map((t) => `${t.toLowerCase()} ${r.hoursByType[t]}u`)
    .join(', ');
  const regels = [
    `Basis = ${r.hoursPerDay} uur per werkdag; 100% betekent een volle dag. Weekenden tellen niet mee — parttime loopt via vrije dagen in de agenda.`,
    `Geregistreerde uren (UrenWerk) zijn leidend. Staat er niets, dan schatten we per type: ${typeUitleg}; overige taken ${r.fallbackHours}u.`,
    'Meetings komen uit de échte Outlook-agenda (exacte duur). Airtable-afspraken worden overgeslagen, anders tellen ze dubbel.',
    r.verdeling === 'opvullen'
      ? `Een meerdaagse taak vult de werkdagen in zijn bereik op naar rato van de ruimte die er nog is: staat er donderdag al 6u vast, dan gaat er van een 10-uurs taak over do+vr maar 2u naar donderdag en 8u naar vrijdag. Past het totaal niet binnen ${r.hoursPerDay}u per dag, dan komt de rest er gelijk bovenop — dat de dag overvol is blijft dus gewoon zichtbaar.`
      : 'Meerdaagse taken verdelen hun uren gelijk over de werkdagen in hun bereik, ongeacht wat er al staat.',
    'Loopt een taak verder dan de zichtbare periode, dan telt alleen het deel van de uren mee dat bij de zichtbare dagen hoort — 160 uur verlof over vier weken is 8 uur per dag, niet 160 uur in deze week.',
  ];
  regels.push(
    r.excludedStatuses.length
      ? `Taken met status ${r.excludedStatuses.join(', ').replace(/, ([^,]*)$/, ' of $1')} tellen niet mee.`
      : 'Alle taken tellen mee, ongeacht status.'
  );
  regels.push(
    r.skipVanallesuurtjeOnDraaidag
      ? 'Een vanallesuurtje telt niet mee op een dag waarop iemand een draaidag heeft (kantoor-edittijd gebeurt niet op een shoot).'
      : 'Een vanallesuurtje telt óók mee op een draaidag.'
  );
  regels.push(
    'Een vrije dag is geen werkdruk maar afwezigheid: die dag krijgt geen percentage maar "Vrij", er landt niets op (ook geen meetings), en hij valt buiten het weekgemiddelde. Loopt er een meerdaagse taak overheen, dan verdelen zijn uren zich over de dagen dat iemand er wél is.'
  );
  regels.push(
    `Een draaidag is een volle dag op locatie en staat altijd op 100%. Staat er toch ander werk op die dag, dan zie je dat als extra in de tooltip — het cijfer blijft ${r.hoursPerDay}u, want er kan niets bij.`
  );
  return regels;
}

/**
 * Waar werkt iemand aan — projectnummer + klant, want dát wil je in het
 * capaciteitsoverzicht zien; "Montage V2" zegt niets zonder project.
 * Velden komen uit /Api/person-tasks-range; oudere/andere bronnen leveren alleen
 * het samengestelde `project`-label, dus dat is de fallback.
 */
export function taskProjectLabel(task) {
  const nummer = String(task?.projectNumber || '').trim();
  // Afkorting (OMWB, KIWA …) als het team die heeft ingesteld — zie
  // Admin → Klant-afkortingen; lange namen drukten de taaknaam weg.
  const klant = kortKlant(String(task?.client || '').trim());
  if (nummer && klant) return `${nummer} · ${klant}`;
  if (nummer) return `${nummer} · ${String(task?.projectName || '').trim()}`.trim().replace(/ ·\s*$/, '');
  if (klant) return klant;
  return String(task?.project || '').trim();
}

/**
 * Projectnaam van een taak, of '' — nooit voor een afspraak (die krijgt geen projectdetails,
 * ook niet als de bron ze per ongeluk meestuurt) en niet voor de terugval "Geen project".
 */
export function taskProjectNaam(task) {
  if (!task || task.type === 'Afspraak' || task.afgeschermd || task.bronAgenda) return '';
  const naam = String(task.projectName || '').trim();
  return /^geen project$/i.test(naam) ? '' : naam;
}

const kortAf = (s, max) => (s.length > max ? `${s.slice(0, max - 1).trimEnd()}…` : s);
const bevat = (hooiberg, naald) => String(hooiberg || '').toLowerCase().includes(naald.toLowerCase());

/**
 * Tweede regel van een projectblok (wiki Rooster 27 sep): "projectnaam · taak", met de
 * projectnaam ingekort. Staat de projectnaam al in de taaknaam ("Montage DEF - Compilatie
 * diversiteit en inclusie") of al in de bovenregel (nummer · projectnaam, zonder klant), dan
 * niet dubbel.
 * @param tekst  wat er na de projectnaam komt (standaard de taaknaam)
 * @param namen  taaknamen om op dubbel te controleren (standaard [task.name])
 */
export function taskProjectTaakRegel(task, { tekst = task?.name, namen = [task?.name], max = 26 } = {}) {
  const taak = String(tekst || '').trim();
  const naam = taskProjectNaamKort(task, { namen, max });
  return naam ? (taak ? `${naam} · ${taak}` : naam) : taak;
}

/**
 * De projectnaam ingekort voor in een blok, of '' als hij er niets toevoegt: geen naam, al in
 * de taaknaam/-namen, of al in de bovenregel (nummer · projectnaam, zonder klant).
 */
export function taskProjectNaamKort(task, { namen = [task?.name], max = 26 } = {}) {
  const naam = taskProjectNaam(task);
  if (!naam) return '';
  const dubbel = namen.length > 0 && namen.every((n) => bevat(n, naam));
  if (dubbel || bevat(taskProjectLabel(task) || naam, naam)) return '';
  return kortAf(naam, max);
}

const isAfgeschermd = (task) => !task || task.type === 'Afspraak' || task.afgeschermd || task.bronAgenda;

/**
 * Project voluit voor een hover: "9736 · Enexis — Compilatie diversiteit en inclusie".
 * Een afspraak: '' (nooit projectdetails, ook niet als de bron ze meestuurt).
 */
export function taskProjectVoluit(task) {
  if (isAfgeschermd(task)) return '';
  const label = taskProjectLabel(task) || String(task?.projectName || '').trim();
  const naam = taskProjectNaam(task);
  return naam && !bevat(label, naam) ? `${label ? `${label} — ` : ''}${naam}` : label;
}

/** "PL: Ton" voor een hover, of '' (afspraken nooit). */
export function taskPlTekst(task) {
  if (isAfgeschermd(task)) return '';
  const pl = Array.isArray(task.projectleider) ? task.projectleider.join(', ') : String(task.projectleider || '').trim();
  return pl ? `PL: ${pl}` : '';
}

/**
 * Project als nummer + projectnaam, met de klant apart (bijzaak). "9685 · Alliander"
 * zei niet om wélk project het ging (Joris, 26 sep: "alleen klantnaam is niet genoeg").
 * Zonder projectnaam: nummer + klant als label, zoals taskProjectLabel.
 * @returns {{ label: string, klant: string }}
 */
export function taskProjectDelen(task) {
  const nummer = String(task?.projectNumber || '').trim();
  const naam = String(task?.projectName || '').trim();
  const klant = kortKlant(String(task?.client || '').trim());
  if (naam) return { label: nummer ? `${nummer} · ${naam}` : naam, klant };
  if (nummer || klant) return { label: [nummer, klant].filter(Boolean).join(' · '), klant: '' };
  return { label: String(task?.project || '').trim(), klant: '' };
}

const iso = (d) => {
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0'), day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

// Werkdagen (ma-vr) in [start,end] als ISO-array.
function workingDaysInRange(startStr, endStr) {
  const out = [];
  const start = new Date(String(startStr).split('T')[0]);
  const end = new Date(String(endStr || startStr).split('T')[0]);
  if (isNaN(start) || isNaN(end)) return out;
  const cur = new Date(start);
  while (cur <= end) {
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6) out.push(iso(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const isVrij = (t) => {
  const prio = t.prio || t.status || '';
  return t.type === 'Vrije dag' || t.type === 'Vrij/intern' || prio === 'Vrije dag' || prio === 'Vrij/intern';
};
const isVanalles = (t) => t.type === 'Vanallesuurtje' || (t.prio || t.status) === 'Vanallesuurtje';

/** Uren die de berekening voor één taak aanhoudt (UrenWerk, anders de inschatting). */
export function effectiveTaskHours(task, rules) {
  const r = normalizeCapacityRules(rules);
  const h = Number(task?.hours) || 0;
  if (h > 0) return { val: h, estimated: false };
  return { val: r.hoursByType[task?.type] ?? r.fallbackHours, estimated: true };
}

/** Dagen die geen gewoon percentage zijn — zie `computeDayStates`. */
export const DAGSTAAT = { VRIJ: 'vrij', DRAAIDAG: 'draaidag', DRAAIDAG_OPTIE: 'draaidag_optie' };

/**
 * Is dit een draaidag die nog een optie is? Twee derde van de draaidagen in de
 * agenda is dat: de PL heeft een dag vastgehouden, maar de klant heeft nog niet
 * bevestigd. De titel begint dan met "OPTIE" ("OPTIE: Michel + Robin || Ardex").
 * Ze tellen wél mee als bezetting — anders plan je er dwars doorheen — maar het
 * scherm hoort te laten zien dat er nog beweging in zit.
 */
export const isDraaidagOptie = (taak) => /^\s*optie\b/i.test(String(taak?.name || taak?.naam || ''));

/** Beide draaidagsoorten vullen de dag; alleen de weergave verschilt. */
export const isDraaidagStaat = (staat) => staat === DAGSTAAT.DRAAIDAG || staat === DAGSTAAT.DRAAIDAG_OPTIE;

const isVrijeDag = (t) => t?.type === 'Vrije dag' || (t?.prio || t?.status) === 'Vrije dag';
const isDraaidag = (t) => t?.type === 'Draaidag' || (t?.prio || t?.status) === 'Draaidag';
// Halve draaidag (Joris, 1 sep): een draaidag met expliciet minder uren dan een
// volle dag (bv. 4u) claimt niet de hele dag — de rest blijft planbaar.
const isVolleDraaidag = (t, hoursPerDay = 8) => {
  if (!isDraaidag(t)) return false;
  const h = Number(t?.hours) || 0;
  return !(h > 0 && h < hoursPerDay);
};

/**
 * Welke dagen zijn geen gewone werkdag voor deze persoon?
 *
 * Een **vrije dag** is geen werkdruk maar het ontbreken van beschikbaarheid. Hij stond
 * in Airtable als taak van 8 uur (bij een week vakantie zelfs één record van 40 uur), en
 * die uren telden gewoon mee — iemand die drie dagen vrij was zag er overbelast uit.
 * Een **draaidag** is een volle dag op locatie: 100%, en er kan niets bij.
 *
 * Let op de scheiding met `Vrij/intern`: dát is intern werk (en de PrioNr die de échte
 * vrije-dag-records dragen), dus alleen het *type* `Vrije dag` telt hier. Op de data van
 * 13 aug 2026 waren dat 961 toekomstige records; de 705 met PrioNr 'Gearchiveerd' hebben
 * geen enkele assignee en komen dus sowieso op niemands tijdlijn terecht.
 *
 * @returns { 'YYYY-MM-DD': 'vrij' | 'draaidag' }
 */
export function computeDayStates(tasks, workingDayISOs, rules = {}) {
  const r = normalizeCapacityRules(rules);
  const uitgesloten = new Set(r.excludedStatuses);
  const inBeeld = new Set(workingDayISOs);
  const staat = {};

  (tasks || []).forEach((t) => {
    if (!t?.startDate || t.isComplete === true) return;
    // Dezelfde uitsluiting als de urenberekening: wat de planner heeft afgevinkt of
    // gearchiveerd mag geen dag claimen die het cijfer eronder negeert.
    if (uitgesloten.has(t.prio || t.status || '')) return;
    const vrij = isVrijeDag(t);
    if (!vrij && !isVolleDraaidag(t, r.hoursPerDay)) return;

    workingDaysInRange(t.startDate, t.endDate).forEach((d) => {
      if (!inBeeld.has(d)) return;
      // Vrij wint van draaidag: ben je er niet, dan sta je ook niet op de set.
      if (vrij) { staat[d] = DAGSTAAT.VRIJ; return; }
      if (staat[d] === DAGSTAAT.VRIJ) return;
      // Een vaste draaidag wint van een optie: staat er allebei iets op die dag,
      // dan is de dag echt bezet en niet "misschien".
      const soort = isDraaidagOptie(t) ? DAGSTAAT.DRAAIDAG_OPTIE : DAGSTAAT.DRAAIDAG;
      if (staat[d] !== DAGSTAAT.DRAAIDAG) staat[d] = soort;
    });
  });

  return staat;
}

/**
 * Belasting per dag voor één persoon, mét de bijzondere dagen erbij.
 *
 * @param tasks           taken uit /person-tasks-range (met type/status/hours/start/end)
 * @param workingDayISOs  de zichtbare werkdagen (ISO-strings) waarvoor we rekenen
 * @param calHoursByDate  { 'YYYY-MM-DD': meetingUren } uit de echte agenda
 * @param rules           regelset (zie DEFAULT_CAPACITY_RULES); ontbrekend = standaard
 * @returns {{
 *   uren:  { 'YYYY-MM-DD': uren },
 *   staat: { 'YYYY-MM-DD': 'vrij' | 'draaidag' },
 *   extra: { 'YYYY-MM-DD': uren },  // werk dat bovenop een draaidag stond
 *   perTaak: Map<taak, { 'YYYY-MM-DD': uren }>  // hoe elke meetellende taak is verdeeld
 * }}
 * `perTaak` is dezelfde verdeling als in `uren` (zelfde opvullen, afwezige dagen en
 * uitsluitingen), zodat een uitklap per taak optelt tot de capaciteitsrij. Sleutel is het
 * taakobject zelf; taken die niet meetellen (afgerond, afspraak, vrije/volle draaidag)
 * staan er niet in.
 */
export function computeDayLoad(tasks, workingDayISOs, calHoursByDate = {}, rules = {}) {
  const r = normalizeCapacityRules(rules);
  const uitgesloten = new Set(r.excludedStatuses);
  const staat = computeDayStates(tasks, workingDayISOs, r);
  const cap = {};
  // Per dag de opbouw van het getal: [{label, uren}] — voor de hover-uitleg
  // ("hoe kom je aan 144%?") zodat het naast Airtable te leggen is (Joris, 1 sep).
  const opbouw = {};
  workingDayISOs.forEach((d) => { cap[d] = 0; opbouw[d] = []; });
  const perTaak = new Map();
  const tel = (d, uren, label, task) => {
    cap[d] += uren;
    if (uren > 0.005) opbouw[d].push({ label, uren });
    if (task && uren > 0) {
      if (!perTaak.has(task)) perTaak.set(task, {});
      const v = perTaak.get(task);
      v[d] = (v[d] || 0) + uren;
    }
  };

  // Draaidag-dagen van deze persoon (voor de vanallesuurtje-regel).
  const draaidagDays = new Set(
    Object.entries(staat).filter(([, s]) => isDraaidagStaat(s)).map(([d]) => d)
  );
  // Op een vrije dag landt niets: geen taak, geen meeting. Die dag is geen 0% "nog niks
  // gepland" maar "niet beschikbaar", en het verschil moet ook in de cijfers zitten —
  // anders vult de planner hem alsnog.
  const beschikbaar = (d) => cap[d] !== undefined && staat[d] !== DAGSTAAT.VRIJ;

  // Agenda-uren staan vast en gaan er als eerste in: bij 'opvullen' bepalen ze mee
  // hoeveel ruimte een meerdaagse taak op die dag nog heeft. Stonden ze achteraan (zoals
  // eerst), dan zou een taak "ruimte" zien die al door meetings bezet is.
  Object.entries(calHoursByDate || {}).forEach(([d, hrs]) => {
    if (beschikbaar(d)) tel(d, Number(hrs) || 0, 'Agenda (Outlook-afspraken)');
  });

  const telt = (task) => {
    if (!task.startDate) return false;
    if (task.type === 'Afspraak') return false;   // meetings uit de agenda (calHoursByDate)
    // De vrije dag en de draaidag zijn de dagstáát; hun uren nog eens als werkdruk
    // meetellen zou de dag dubbel vullen.
    // Een hálve draaidag telt wél als uren mee (de dag is niet vol).
    if (isVrijeDag(task) || isVolleDraaidag(task, r.hoursPerDay)) return false;
    // Afgeronde/opgestuurde taken tellen niet mee — de uitklap verbergt ze al; zonder
    // deze check telde de berekening ze stiekem wél mee, waardoor het percentage niet
    // te herleiden was naar de zichtbare taken.
    return !uitgesloten.has(task.prio || task.status || '');
  };

  // Dagen waar deze taak daadwerkelijk op landt: binnen het venster, niet op een vrije
  // dag, en niet weggevallen door de vanallesuurtje-op-draaidag-regel.
  // Een MEERDAAGSE taak schuift ook om draaidagen heen: die dag is vol (iemand staat
  // op locatie), dus de uren horen op de andere dagen van het venster te landen.
  // Een taak die expliciet alléén op zo'n dag gepland is laten we juist stáán —
  // die wordt als extra (⚠) gemeld in plaats van stilletjes te verdwijnen.
  const dagenVoor = (task) => {
    const alle = workingDaysInRange(task.startDate, task.endDate);
    const gefilterd = alle.filter((d) => {
      if (!beschikbaar(d)) return false;
      if (alle.length > 1 && draaidagDays.has(d)) return false;
      if (isVanalles(task) && r.skipVanallesuurtjeOnDraaidag && draaidagDays.has(d)) return false;
      return true;
    });
    if (gefilterd.length) return gefilterd;
    // Alles weggefilterd (bv. taak valt volledig op draaidagen): terugvallen op de
    // beschikbare dagen zonder draaidag-uitsluiting, zodat de uren als extra zichtbaar
    // blijven in plaats van uit de telling te vallen.
    return alle.filter((d) => beschikbaar(d));
  };

  // Loopt een taak verder dan het zichtbare venster, dan hoort hier maar een déél van
  // zijn uren: 160 uur verlof over vier weken is 8 uur per werkdag, niet 160 uur op de
  // twee zichtbare dagen (dat gaf rijen van 500-1000%). De uitklap spreidt de uren al
  // over de hele looptijd; de rij moet dezelfde som maken.
  const urenVoor = (task) => {
    const h = Number(task.hours) || 0;
    const totaal = h > 0 ? h : (r.hoursByType[task.type] ?? r.fallbackHours);
    const alle = workingDaysInRange(task.startDate, task.endDate);
    if (alle.length <= 1) return totaal;
    const inVenster = alle.filter((d) => cap[d] !== undefined).length;
    return totaal * (inVenster / alle.length);
  };

  const meetellend = (tasks || []).filter(telt).map((task) => ({ task, dagen: dagenVoor(task) }))
    .filter((x) => x.dagen.length > 0);

  // De bijzondere dagen krijgen hun vaste waarde pas ná het verdelen: tijdens het rekenen
  // is een draaidag nog gewoon een dag met ruimte, zodat een taak die eroverheen loopt
  // zijn uren naar de andere dagen kan schuiven.
  const afmaken = () => {
    const extra = {};
    workingDayISOs.forEach((d) => {
      if (staat[d] === DAGSTAAT.VRIJ) {
        cap[d] = 0; opbouw[d] = [];
        perTaak.forEach((v) => { delete v[d]; });
        return;
      }
      if (!isDraaidagStaat(staat[d])) return;
      // Een draaidag ís de dag: 100%, niet 138% omdat er ook nog een montage op stond.
      // Wat er tóch bovenop staat gooien we niet weg maar zetten we apart, zodat de
      // tooltip het conflict kan melden in plaats van het stil te maskeren.
      if (cap[d] > 0) extra[d] = cap[d];
      cap[d] = r.hoursPerDay;
      opbouw[d] = [{
        label: staat[d] === DAGSTAAT.DRAAIDAG_OPTIE
          ? 'Draaidag (optie) — telt als volle dag zolang de klant niet heeft afgezegd'
          : 'Draaidag — telt als volle dag',
        uren: r.hoursPerDay,
      }, ...opbouw[d]];
    });
    return { uren: cap, staat, extra, opbouw, perTaak };
  };

  const taakLabel = (task) => {
    const nummer = String(task?.projectNumber || '').trim();
    return `${nummer ? `${nummer} · ` : ''}${task?.name || task?.type || 'Taak'}`;
  };

  if (r.verdeling === 'gelijk') {
    meetellend.forEach(({ task, dagen }) => {
      const perDag = urenVoor(task) / dagen.length;
      dagen.forEach((d) => { tel(d, perDag, taakLabel(task), task); });
    });
    return afmaken();
  }

  // 'opvullen'. Eendaagse taken liggen vast en gaan eerst — die hebben geen keuze in
  // waar ze landen en bepalen dus de ruimte voor de rest.
  const eendaags = meetellend.filter((x) => x.dagen.length === 1);
  const meerdaags = meetellend.filter((x) => x.dagen.length > 1);
  eendaags.forEach(({ task, dagen }) => { tel(dagen[0], urenVoor(task), taakLabel(task), task); });

  // De uitkomst mag niet van de volgorde in Airtable afhangen: kortste bereik eerst
  // (die heeft de minste bewegingsvrijheid), dan op datum en naam.
  meerdaags.sort((a, b) =>
    a.dagen.length - b.dagen.length
    || String(a.task.startDate).localeCompare(String(b.task.startDate))
    || String(a.task.name || a.task.id || '').localeCompare(String(b.task.name || b.task.id || ''))
  );

  meerdaags.forEach(({ task, dagen }) => {
    const uren = urenVoor(task);
    const ruimte = dagen.map((d) => Math.max(0, r.hoursPerDay - cap[d]));
    const totaleRuimte = ruimte.reduce((s, v) => s + v, 0);

    if (totaleRuimte <= 0) {
      // Alle dagen zitten al vol; dan is er niets te verdelen naar rato en valt de taak
      // terug op een gelijke verdeling — de overbelasting blijft zo wél zichtbaar.
      dagen.forEach((d) => { tel(d, uren / dagen.length, taakLabel(task), task); });
      return;
    }
    if (uren <= totaleRuimte) {
      dagen.forEach((d, i) => { tel(d, uren * (ruimte[i] / totaleRuimte), taakLabel(task), task); });
      return;
    }
    // Past niet: eerst elke dag tot vol, de rest gelijk erbovenop.
    const rest = (uren - totaleRuimte) / dagen.length;
    dagen.forEach((d, i) => { tel(d, ruimte[i] + rest, taakLabel(task), task); });
  });

  return afmaken();
}

/**
 * Alleen de uren, voor plekken die de dagstaat (nog) niet tonen.
 * Bewust een aparte functie en geen tweede signatuur: zo blijft op elke aanroepplek
 * zichtbaar of dat scherm de vrije dagen wél of niet apart behandelt.
 */
export function computeDailyCapacityHours(tasks, workingDayISOs, calHoursByDate = {}, rules = {}) {
  return computeDayLoad(tasks, workingDayISOs, calHoursByDate, rules).uren;
}

/**
 * Weekgemiddelde % over de zichtbare werkdagen (zelfde noemer in alle pagina's).
 *
 * Vrije dagen vallen uit teller én noemer: een week met twee vakantiedagen waarin je de
 * andere drie vol staat is 100% bezet, niet 60% — anders lijkt er ruimte die er niet is.
 */
export function weekAveragePct(capHoursByDate, workingDayISOs, hoursPerDay = HOURS_PER_DAY, dayStates = {}) {
  const dagen = workingDayISOs.filter((d) => dayStates[d] !== DAGSTAAT.VRIJ);
  if (!dagen.length) return 0;
  const noemer = positief(hoursPerDay, HOURS_PER_DAY);
  const total = dagen.reduce((s, d) => s + (capHoursByDate[d] || 0), 0);
  return Math.round((total / (dagen.length * noemer)) * 100);
}

export const pctOfDay = (hours, hoursPerDay = HOURS_PER_DAY) =>
  Math.round(((hours || 0) / positief(hoursPerDay, HOURS_PER_DAY)) * 100);

export { isVrij, isVanalles };
